import { Task } from 'src/tmc-yard/components/task-assignment/model/task';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, NgForm } from '@angular/forms';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ConfigurationService, DialogService, ResponseMessage, ResponseStatus } from '@sdbn/appclient';
import { TranslationService } from '@sdbn/common';
import { ConfirmDialogComponent, ConfirmDialogData } from 'src/app/shared/confirm-dialog/confirm.dialog.component';
import { TmcContextService } from 'src/tmc-common/services/tmc-context.service';
import { TMCClientParameters } from 'src/tmc-common/tmc-client-parameters';
import { StationAssignmentStrategy } from 'src/tmc-yard/common/station-assignment-strategy/station-assignment-strategy.model';
import { StructureSelectionService } from 'src/tmc-yard/common/structure-selection/structure-selection-dialog/structure-selection-dialog-service';
import { StructureSelection } from 'src/tmc-yard/common/structure-selection/structure-selection.model';
import { TaskAssignmentService } from 'src/tmc-yard/components/task-assignment/service/task-assignment.service';
import { TransportRoutingService } from '../service/transport-routing.service';
import { ActionType, Route, RoutingPosition, RoutingPositionStatus, StationLevel, StationType, TransportRouting } from '../routing-position.model';

export interface ActionTypeOption {
  option: ActionType;
  name: string;
}

@Component({
  selector: 'app-routing',
  templateUrl: './routing.component.html',
  styleUrls: ['./routing.component.scss']
})
export class RoutingComponent implements OnInit, AfterViewInit {

  isLoading = false;
  isDirty = false;
  isValid = false;

  transportRouting = new TransportRouting({});

  responseMessages: ResponseMessage[];

  initialChangeRouteManually = false;

  clientParameters = TMCClientParameters;

  shuttleTransportsEnabled = false;

  routingPositionStatus = RoutingPositionStatus;

  form = new FormGroup({
    useAsShuttle: new FormControl(false),
    changeRouteManually: new FormControl(false)
  });

  get isSaveDisabled() {
    return this.isLoading || !this.isValid || !this.isDirty;
  }

  constructor(
    public dialogRef: MatDialogRef<RoutingComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public translationService: TranslationService,
    public cps: ConfigurationService,
    private transportRoutingService: TransportRoutingService,
    private dialogService: DialogService,
    private dialog: MatDialog,
    private structureSelectionService: StructureSelectionService,
    private tmcContextService: TmcContextService,
    private taskAssignmentService: TaskAssignmentService) {


    if (this.cps.isEnabled(this.clientParameters.FEATURES_SHUTTLE_TRANSPORTS, false)) {
      this.shuttleTransportsEnabled = true;
    }

    this.isLoading = true;
    this.transportRoutingService.getTransportRouting({ id: data.transportId }).subscribe(response => {
      const transportRouting = new TransportRouting(response);
      this.transportRouting = transportRouting;
      this.initialChangeRouteManually = this.transportRouting.changeRouteManually;
      this.transportRouting.reloadRouteEnabled = this.checkIsReloadRouteEnabled();

      this.form.patchValue({
        useAsShuttle: this.transportRouting.useAsShuttle,
        changeRouteManually: this.transportRouting.changeRouteManually
      });

      this.form.markAllAsTouched();
      this.isLoading = false;
      setTimeout(() => this.markFormAsTouched(), 250);
    }, error => {
      console.log(error);
      this.isLoading = false;
    }, () => {
      console.log();
    }
    );
  }

  ngAfterViewInit(): void {
    this.markFormAsTouched();
  }

  ngOnInit() {
    this.form.valueChanges.subscribe(values => {
      this.transportRouting.useAsShuttle = values.useAsShuttle;
      this.transportRouting.changeRouteManually = values.changeRouteManually;
    });
  }

  private checkIsReloadRouteEnabled() {

    for (const rp of this.transportRouting.routingPositions) {
      if (rp.routingPositionStatus !== RoutingPositionStatus.PLANNED) {
        return false;
      }
    }

    return true;
  }

  cancel() {
    this.dialogRef.close();
  }

  save() {

    this.responseMessages = [];

    this.isLoading = true;
    this.transportRoutingService.save(this.transportRouting).subscribe(response => {
      if (response.status === ResponseStatus.SUCCESS) {
        this.dialogRef.close();
      } else {
        this.responseMessages = response.responseMessages;
      }
      this.isLoading = false;
    }, error => {
      console.log(error);
      this.isLoading = false;
    });
  }

  isActionTypeDisabled(position: RoutingPosition) {
    return !this.transportRouting.changeRouteManually || position.stationType === StationType.WAITING_AREA;
  }

  isCargoTypeDisabled(position: RoutingPosition) {
    return position.stationType === StationType.WAITING_AREA;
  }

  isWaitingForRetrieveDisabled(position: RoutingPosition) {
    return !this.transportRouting.changeRouteManually || position.stationType === StationType.WAITING_AREA;
  }

  isPredictedDurationDisabled(position: RoutingPosition) {
    return !this.transportRouting.changeRouteManually || position.stationType === StationType.WAITING_AREA;
  }

  onModelChanged() {
    console.log('onModelChanged');
    this.validate();
    this.isDirty = true;
  }

  private validate() {
    this.isValid = this.checkPositions();
  }

  private checkPositions(): boolean {

    if (this.transportRouting.routingPositions.length === 0) {
      return false;
    }

    for (const rp of this.transportRouting.routingPositions) {

      if (rp.stationId == null) {
        return false;
      }

      if (rp.actionType == null) {
        return false;
      }

      if (this.isCargoRequired(rp) && (rp.cargo === null || rp.cargo.length === 0)) {
        return false;
      }

      if (rp.stationLevel === StationLevel.STATION_SET && rp.stationAssignmentStrategy == null) {
        return false;
      }

      if (rp.predictedDuration != null && rp.predictedDuration < 0) {
        return false;
      }
    }
    return true;
  }

  drop(event: CdkDragDrop<string[]>) {
    const offset = this.getNonChangeableRoutings().length;
    moveItemInArray(this.transportRouting.routingPositions, event.previousIndex + offset, event.currentIndex + offset);
    this.updatePositionsIndex();
    this.validate();
    this.isDirty = true;
  }

  updatePositionsIndex() {
    for (let i = 0; i < this.transportRouting.routingPositions.length; i++) {
      this.transportRouting.routingPositions[i].index = i;
    }
  }

  removePosition(position: RoutingPosition) {
    const index = this.transportRouting.routingPositions.indexOf(position);

    this.transportRouting.routingPositions.splice(index, 1);

    if (position.id != null) {
      this.transportRouting.deletedRoutingPositions.push(position);
    }

    if (this.transportRouting.routingPositions.length === 0) {
      this.addPosition();
    }
    this.updatePositionsIndex();
    this.validate();
    this.isDirty = true;
  }


  addPosition() {
    const position = new RoutingPosition({});
    position.index = this.transportRouting.routingPositions.length;
    position.actionType = ActionType.UNLOAD;
    position.routingPositionStatus = RoutingPositionStatus.PLANNED;
    this.transportRouting.routingPositions.push(position);
    this.validate();
    this.isDirty = true;
  }

  isSubmitDisabled(): boolean {
    return false;
  }

  getNonChangeableRoutings(): RoutingPosition[] {
    return this.transportRouting.routingPositions.filter(rp => rp.routingPositionStatus !== RoutingPositionStatus.PLANNED);
  }

  getChangeableRoutings(): RoutingPosition[] {
    return this.transportRouting.routingPositions.filter(rp => rp.routingPositionStatus === RoutingPositionStatus.PLANNED);
  }

  isCargoRequired(position: RoutingPosition) {
    return position.cargoMandatory || this.cps.isRequired('visitoverview.register.minlength.cargo', true);
  }

  isStationAssignmentStrategyRequired(position: RoutingPosition): boolean {
    return position.stationLevel == StationLevel.STATION_SET;
  }

  isStationAssignmentStrategyDisabled(position: RoutingPosition): boolean {
    return this.isLoading || !this.transportRouting.changeRouteManually || position.stationLevel !== StationLevel.STATION_SET;
  }


  getAssignedTasksCount(position: RoutingPosition) {
    return position.tasks == null ? 0 : position.tasks.length;
  }

  positionUpdated(position: RoutingPosition) {
    console.log('positionUpdated', position);
    this.onModelChanged();
  }

  reloadRoute() {

    if (!this.transportRouting.reloadRouteEnabled) {

      this.dialogService.showInfoDialog(
        'tmc.transport.routing.reload-route.info-dialog.title',
        'tmc.transport.routing.reload-route.info-dialog.message'
      );
      return;
    }

    const dialogData: ConfirmDialogData = {
      titleKey: 'tmc.transport.routing.reload-route.confirm-dialog.title',
      messageKey: 'tmc.transport.routing.reload-route.confirm-dialog.message',
      confirm: () => {
        this.transportRoutingService.reloadRoute({ id: this.transportRouting.id }).subscribe(
          loadedRoute => {
            const route = new Route(loadedRoute);
            // alle aktuellen positionen entfernen
            for (const p of this.transportRouting.routingPositions) {
              if (p.id != null) {
                this.transportRouting.deletedRoutingPositions.push(p);
              }
            }
            this.transportRouting.routingPositions = [];
            for (const p of route.routingPositions) {
              this.transportRouting.routingPositions.push(p);
            }

            // Wenn changeRouteManually in der route als true gespeichert war,
            // dann muss die Maske als dirty markiert werden damit ein Speichern des geänderten flags möglich ist
            if (this.initialChangeRouteManually === true) {
              this.isDirty = true;
            } else {
              this.isDirty = false;
            }
            this.transportRouting.changeRouteManually = false;
            this.updatePositionsIndex();
            this.validate();
            this.markFormAsTouched();
          }
        );
      }
    };
    this.dialog.open(ConfirmDialogComponent, {
      data: dialogData
    });
  }

  private markFormAsTouched() {
    Object.keys(this.form.controls).forEach(key => {
      this.form.controls[key].markAsTouched();
    });
  }
}

